import React, {SyntheticEvent, ReactNode, useContext, useState} from 'react';
import {injectIntl, FormattedMessage} from 'react-intl';
import classnames from 'classnames';
import delay from 'lodash.delay';
import {DropdownControlled} from '@homeaway/react-dropdown';

import {
    TrackingContext,
    TrackingContextValue,
} from '@homeaway/react-event-tracking';

import InstantConfirmationFilter from '../InstantConfirmationFilter/InstantConfirmationFilter';
import BedroomsFilter, {
    getBedroomValue,
    getBedroomLabel,
} from '../BedroomsFilter/BedroomsFilter';
import DropdownPanel from '../DropdownPanel/DropdownPanel';
import PriceFilter from '../PriceFilter/PriceFilter';
import FormattedCurrencyRange from '../FormattedCurrencyRange/FormattedCurrencyRange';
import {
    getLimitedMaxDisplayPrice,
    getLimitedMaxPrice,
} from '../PriceFilter/utils';
import MoreFiltersPanelDropdown from '../MoreFiltersPanelDropdown/MoreFiltersPanelDropdown';

import './FilterBar.less';

import type {IntlShape} from 'react-intl';

export type FilterDispatchProps = {
    filter: Function;
    onDone: Function;
    resetInputParams: Function;
    syncInputParams: Function;
    updateFilterCheck: Function;
    updateInputParams: Function;
    updatePriceInputValuesValidity: Function;
};

export type FiltersProps = {
    currency: string;
    hasDates: boolean;
    inputParamsAreChanged: boolean;
    inputParamsAreDefault: boolean;
    isMobile: boolean;
    locale: string;
    priceInputValuesAreValid: boolean;
    viewportHeight: number;
    numFiltersPanel?: number | null;
};

export type FilterBarProps = {
    currentBedrooms: number;
    exactBedroomsMatch: boolean;
    hideOverlay: Function;
    inputMaxBedrooms: number;
    inputMaxPrice: number;
    inputMinBedrooms: number;
    inputMinPrice: number;
    isInstantBookingChecked: boolean;
    isMoreFiltersSelected: boolean;
    isOverlayVisible: boolean;
    maxBedrooms: number;
    maxPrice: number;
    minBedrooms: number;
    minPrice: number;
    searchResultsLoading: boolean;
    showOverlay: Function;
    siteName: string;
    topBannersHeight: number;
} & FiltersProps &
    FilterDispatchProps;

type Props = {
    intl: IntlShape;
} & FilterBarProps;

type FilterBarDropdownProps = {
    children: ReactNode;
    dataWdio?: string;
    dropdownClass: string;
    isSelected: boolean;
};

type FilterId =
    | 'bedroomsFilter'
    | 'instantConfirmationFilter'
    | 'moreFilters'
    | 'priceFilter';

type State = {
    dropdownOpen: FilterId;
};

export const BEDROOMS_FILTER_ID = 'bedroomsFilter';
export const INSTANT_CONFIRMATION_FILTER_ID = 'instantConfirmationFilter';
export const MORE_FILTERS_ID = 'moreFilters';
export const PRICE_FILTER_ID = 'priceFilter';

export const EDAP_DROPDOWN_CLICK_LABELS = {
    [BEDROOMS_FILTER_ID]: 'filters-beds-drop',
    [INSTANT_CONFIRMATION_FILTER_ID]: 'filters-instant-confirmation-drop',
    [PRICE_FILTER_ID]: 'filters-price-drop',
};

export const EDAP_DROPDOWN_APPLY_LABELS = {
    [BEDROOMS_FILTER_ID]: 'beds',
    [INSTANT_CONFIRMATION_FILTER_ID]: 'instant_booking',
    [PRICE_FILTER_ID]: 'price',
};

export function FilterBarDropdown({
    children,
    dataWdio,
    dropdownClass,
    isSelected,
}: FilterBarDropdownProps) {
    return (
        <div
            data-wdio={dataWdio}
            className={classnames(`FilterBar__dropdown ${dropdownClass}`, {
                'FilterBar__dropdown--selected': isSelected,
                'FilterBar__dropdown--unselected': !isSelected,
            })}
        >
            {children}
        </div>
    );
}

// eslint-disable-next-line complexity
export function FilterBar(props) {
    /* eslint-disable no-undefined */
    const [dropdownOpen, setDropDownOpen] = useState<FilterId | undefined>(
        undefined
    );

    const edapContext: TrackingContextValue = useContext(TrackingContext);

    const closeDropdowns = () => {
        if (dropdownOpen) {
            props.hideOverlay();
            setDropDownOpen(undefined); /* eslint-disable no-undefined */
        }
    };

    const openDropdown = (key: FilterId) => {
        setDropDownOpen(key);
        props.showOverlay();
    };

    /** End dropdown controllers */

    const filterIsOpen = (id: FilterId): boolean => dropdownOpen === id;

    /**
     * performSearch()
     * Method that accesses `searchParams` action dispatcher
     */
    const performSearch = () => {
        props.filter();
    };

    /**
     * handleInputParamsBedroomsFilterChange()
     * Handles transient-state click interaction with range filters (bedrooms, bathrooms, etc)
     */
    const handleInputParamsBedroomsFilterChange = ({
        minBedrooms,
        maxBedrooms,
    }) => {
        const {updateInputParams} = props;
        updateInputParams({
            minBedrooms,
            maxBedrooms,
        });
    };

    const resetParams = () => {
        edapContext.trigger('generic.event', {
            eventaction: 'all filters cleared',
        });
        props.resetInputParams();
        props.filter();
    };

    const handlePriceChange = (bounds) => {
        const {updateInputParams} = props;
        updateInputParams({
            minPrice: bounds[0] || 0,
            maxPrice: bounds[1],
        });
    };

    const cancel = () => {
        edapContext.trigger('generic.event', {
            eventaction: 'filters canceled',
        });

        props.syncInputParams();
        closeDropdowns();
    };

    const applyFilters = () => {
        const {inputParamsAreChanged, updatePriceInputValuesValidity} = props;
        if (inputParamsAreChanged) {
            if (dropdownOpen && dropdownOpen !== MORE_FILTERS_ID) {
                edapContext.trigger('generic.event', {
                    eventaction: 'filters applied',
                    eventlabel: EDAP_DROPDOWN_APPLY_LABELS[dropdownOpen],
                });
            } else {
                edapContext.trigger('generic.event', {
                    eventaction: 'filters applied',
                });
            }
            performSearch();
        }
        updatePriceInputValuesValidity(true);
        closeDropdowns();
    };

    const toggleDropdown = (key: FilterId) => () => {
        if (key === MORE_FILTERS_ID) {
            edapContext.trigger('generic.event', {
                eventaction: 'filter menu clicked',
            });
        } else {
            edapContext.trigger('generic.event', {
                eventaction: 'filter dropdown clicked',
                eventlabel: EDAP_DROPDOWN_CLICK_LABELS[key],
            });
        }

        if (dropdownOpen === key) {
            applyFilters();
        } else {
            openDropdown(key);
        }
    };

    const handleInstantConfirmationApply = (
        event: SyntheticEvent<HTMLInputElement>
    ) => {
        const {updateFilterCheck} = props;
        if (!event || !event.currentTarget) {
            return;
        }

        const eventlabel = event.currentTarget.checked
            ? 'instant_booking'
            : 'no_instant_booking';

        edapContext.trigger('generic.event', {
            eventaction: 'filters applied',
            eventlabel,
            highfrequency: 'true',
        });

        updateFilterCheck(event.currentTarget);

        performSearch();
        delay(closeDropdowns, 200);
    };

    /**
     * handleInputParamsRangeFilterChange(), e.g. BathroomsFilter
     * Handles transient-state click interaction with range filters (bedrooms, bathrooms, etc)
     */
    const handleInputParamsRangeFilterChange = (values) => {
        const {updateInputParams} = props;
        updateInputParams(values);
    };

    const handleDropdownClear = (paths) => () => {
        props.resetInputParams(paths);
        performSearch();
        closeDropdowns();
    };

    const rangeParamsNotDefault = (
        minParam?: number,
        maxParam?: number | null
    ): boolean => {
        return minParam !== 0 || maxParam !== null;
    };

    const bedroomsDropdownLabel = (showDefault: boolean): string => {
        const {intl, minBedrooms = 0, maxBedrooms} = props;
        const exactBedroomsMatch = minBedrooms === maxBedrooms;

        if (showDefault) {
            return intl.formatMessage({
                defaultMessage: 'Bedrooms',
                description: 'toggle bedrooms filter',
                id: 'bedroomsLabel',
            });
        }
        return `${getBedroomValue(
            minBedrooms,
            exactBedroomsMatch,
            intl.formatMessage
        )} ${getBedroomLabel(
            minBedrooms,
            exactBedroomsMatch,
            intl.formatMessage
        )}`;
    };

    const priceDropdownLabel = (showDefault: boolean) => {
        const {intl, minPrice, maxPrice, currency, locale} = props;
        const limitedMaxPrice = getLimitedMaxPrice(currency);
        const maxAllowedPrice = limitedMaxPrice;
        const maxDisplayPrice = getLimitedMaxDisplayPrice(
            maxAllowedPrice,
            maxPrice
        );
        const isMax = maxDisplayPrice >= maxAllowedPrice;

        if (showDefault) {
            return intl.formatMessage({
                defaultMessage: 'Price',
                description: 'filter on the prices of properties',
                id: 'filterBarPriceLabel',
            });
        }

        return (
            <FormattedCurrencyRange
                minPrice={minPrice || 0}
                maxPrice={maxDisplayPrice}
                currency={currency}
                isMax={isMax}
                locale={locale}
            />
        );
    };

    /**
     * TODO: fix localization in <Dropdown label> prop
     */
    const {
        currency,
        hasDates,
        inputMaxBedrooms,
        inputParams,
        isMobile,
        canFit3Columns,
        canFit4Columns,
        inputMaxPrice,
        inputMinBedrooms = 0,
        inputMinPrice,
        inputParamsAreChanged,
        inputParamsAreDefault,
        intl,
        isInstantBookingChecked,
        isMoreFiltersSelected,
        isOverlayVisible,
        filterCountDataProvider,
        maxBedrooms,
        maxPrice,
        minBedrooms,
        minPrice,
        numFiltersPanel,
        searchResultsLoading,
        selectedFilters,
        topBannersHeight,
        locale,
        priceInputValuesAreValid,
        updatePriceInputValuesValidity,
        updateInputParams,
        updateFilterCheck,
    } = props;

    // Dropdown open states
    const isBedroomsFilterOpen = filterIsOpen(BEDROOMS_FILTER_ID);
    const isPriceDropdownOpen = filterIsOpen(PRICE_FILTER_ID);
    const isInstantConfirmationFilterOpen = filterIsOpen(
        INSTANT_CONFIRMATION_FILTER_ID
    );
    const isMoreFiltersOpen = filterIsOpen(MORE_FILTERS_ID);

    // Dropdown active states
    const isBedroomsFilterActive = rangeParamsNotDefault(
        minBedrooms,
        maxBedrooms
    );
    const isPriceFilterActive = rangeParamsNotDefault(minPrice, maxPrice);

    // Dropdown clear visible states
    const isBedroomsFilterClearVisible = rangeParamsNotDefault(
        inputMinBedrooms,
        inputMaxBedrooms
    );
    const isPriceFilterClearVisible =
        !priceInputValuesAreValid ||
        rangeParamsNotDefault(inputMinPrice, inputMaxPrice);

    return (
        <div className="FilterBar" data-wdio="FilterBar">
            <FilterBarDropdown
                dropdownClass="PriceDropdown"
                dataWdio="PriceDropdown"
                isSelected={isPriceFilterActive}
            >
                <DropdownControlled
                    id={PRICE_FILTER_ID}
                    label={priceDropdownLabel(!isPriceFilterActive)}
                    onDropdownClick={toggleDropdown(PRICE_FILTER_ID)}
                    onBlur={applyFilters}
                    onTouch={applyFilters}
                    open={isPriceDropdownOpen}
                >
                    {isPriceDropdownOpen && (
                        <DropdownPanel
                            onApply={applyFilters}
                            onClear={handleDropdownClear([
                                'minPrice',
                                'maxPrice',
                            ])}
                            showButtons
                            clearVisible={isPriceFilterClearVisible}
                            submitDisabled={!priceInputValuesAreValid}
                        >
                            <PriceFilter
                                currency={currency}
                                hasDates={hasDates}
                                onPriceChange={handlePriceChange}
                                minPrice={inputMinPrice}
                                maxPrice={inputMaxPrice}
                                locale={locale}
                                className="PriceFilterDropdown_PriceFilter"
                                priceInputValuesAreValid={
                                    priceInputValuesAreValid
                                }
                                updatePriceInputValuesValidity={
                                    updatePriceInputValuesValidity
                                }
                            />
                        </DropdownPanel>
                    )}
                </DropdownControlled>
            </FilterBarDropdown>
            <FilterBarDropdown
                dropdownClass="BedroomsDropdown"
                dataWdio="BedroomsDropdown"
                isSelected={isBedroomsFilterActive}
            >
                <DropdownControlled
                    id={BEDROOMS_FILTER_ID}
                    label={bedroomsDropdownLabel(!isBedroomsFilterActive)}
                    onDropdownClick={toggleDropdown(BEDROOMS_FILTER_ID)}
                    onBlur={applyFilters}
                    onTouch={applyFilters}
                    open={isBedroomsFilterOpen}
                >
                    {isBedroomsFilterOpen && (
                        <DropdownPanel
                            showButtons
                            onApply={applyFilters}
                            onClear={handleDropdownClear([
                                'minBedrooms',
                                'maxBedrooms',
                            ])}
                            clearVisible={isBedroomsFilterClearVisible}
                        >
                            <BedroomsFilter
                                minBedrooms={inputMinBedrooms}
                                maxBedrooms={inputMaxBedrooms}
                                onChange={handleInputParamsBedroomsFilterChange}
                                className="BedroomsDropdown__BedroomsFilter"
                            />
                        </DropdownPanel>
                    )}
                </DropdownControlled>
            </FilterBarDropdown>
            <FilterBarDropdown
                dropdownClass="InstantConfirmationDropdown"
                dataWdio="InstantConfirmationDropdown"
                isSelected={isInstantBookingChecked}
            >
                <DropdownControlled
                    id={INSTANT_CONFIRMATION_FILTER_ID}
                    label={intl.formatMessage({
                        defaultMessage: 'Instant Confirmation',
                        description: 'toggle instant confirmation properties',
                        id: 'instantConfirmationPrimaryLabel',
                    })}
                    onDropdownClick={toggleDropdown(
                        INSTANT_CONFIRMATION_FILTER_ID
                    )}
                    onBlur={closeDropdowns}
                    onTouch={closeDropdowns}
                    open={isInstantConfirmationFilterOpen}
                >
                    {isInstantConfirmationFilterOpen && (
                        <DropdownPanel>
                            <InstantConfirmationFilter
                                id="instantConfirmation__switch"
                                intl={intl}
                                checked={isInstantBookingChecked}
                                handleChecked={handleInstantConfirmationApply}
                                className="InstantConfirmationDropdown_InstantConfirmationFilter"
                            />
                        </DropdownPanel>
                    )}
                </DropdownControlled>
            </FilterBarDropdown>
            <FilterBarDropdown
                dropdownClass={'MoreFiltersPanelDropdown'}
                isSelected={isMoreFiltersSelected}
            >
                {/* eslint-disable-next-line */}
                {/*
                // @ts-ignore */}
                <MoreFiltersPanelDropdown
                    canFit3Columns={canFit3Columns}
                    canFit4Columns={canFit4Columns}
                    isMobile={isMobile}
                    currency={currency}
                    filter={inputParams.filter}
                    hasDates={hasDates}
                    inputMaxBedrooms={inputMaxBedrooms}
                    inputMaxPrice={inputMaxPrice}
                    inputMinBedrooms={inputMinBedrooms}
                    inputMinPrice={inputMinPrice}
                    inputParamsAreChanged={inputParamsAreChanged}
                    locale={locale}
                    maxBathrooms={inputParams.maxBathrooms}
                    minBathrooms={inputParams.minBathrooms}
                    selectedFilters={selectedFilters}
                    toggleDropdown={toggleDropdown(MORE_FILTERS_ID)}
                    onApplyFilters={applyFilters}
                    onCancel={cancel}
                    onInputParamsRangeFilterChange={
                        handleInputParamsRangeFilterChange
                    }
                    numFiltersPanel={numFiltersPanel}
                    open={isMoreFiltersOpen}
                    searchResultsLoading={searchResultsLoading}
                    topBannersHeight={topBannersHeight}
                    filterCountDataProvider={filterCountDataProvider}
                    updateInputParams={updateInputParams}
                    updateFilterCheck={updateFilterCheck}
                    priceInputValuesAreValid={priceInputValuesAreValid}
                    updatePriceInputValuesValidity={
                        updatePriceInputValuesValidity
                    }
                />
            </FilterBarDropdown>
            {!inputParamsAreDefault && (
                <button
                    className={classnames('FilterBar__clear btn btn-link', {
                        invisible: isOverlayVisible,
                    })}
                    data-wdio="FilterBar__clear"
                    onClick={resetParams}
                >
                    <FormattedMessage
                        id="formModalResetLabel"
                        description="Button to reset filters"
                        defaultMessage="Clear Filters"
                    />
                </button>
            )}
        </div>
    );
}
export default injectIntl(FilterBar);
